import { useEffect, useRef, useState } from "react"
import { useNavigate }       from "react-router-dom"
import { useAuth as useClerkAuth, useClerk } from "@clerk/react"
import { api }               from "@/lib/api-client"
import { isDoctorHost }      from "@/lib/host"
import { track }             from "@/lib/observability"
import { runD2cSignup }      from "@/lib/d2cSignup"
import { sanitizeNextPath }  from "@/lib/safeRedirect"

const ROLE_KEY = "d2c_signup_role"

function brandLooksConfigured(brand: any): boolean {
  if (!brand || typeof brand !== "object") return false
  const fields = ["doctor_name", "doctor_credentials", "display_name",
                  "address_line_1", "address_line_2", "phone", "logo_url"]
  return fields.some(f => typeof brand[f] === "string" && brand[f].trim().length > 0)
}

export default function AuthCallback() {
  const navigate  = useNavigate()
  const { isSignedIn, isLoaded } = useClerkAuth()
  const { handleRedirectCallback } = useClerk()
  const handled   = useRef(false)
  const [provisionError, setProvisionError] = useState<string | null>(null)

  useEffect(() => {
    if (handled.current || provisionError) return
    // If there are OAuth params in the URL, let Clerk process the redirect first.
    // Redirect back to /app/auth/callback (preserving next) so provisioning runs.
    if (window.location.search.includes("__clerk") || window.location.hash.includes("__clerk")) {
      handled.current = true
      const searchParams = new URLSearchParams(window.location.search)
      const next = searchParams.get("next")
      const safeNext = sanitizeNextPath(next, "")
      const callbackDest = safeNext ? `/app/auth/callback?next=${encodeURIComponent(safeNext)}` : "/app/auth/callback"
      handleRedirectCallback({
        signInForceRedirectUrl: callbackDest,
        signUpForceRedirectUrl: callbackDest,
      }).catch(() => navigate("/login", { replace: true }))
      return
    }
    if (!isLoaded || handled.current) return
    if (!isSignedIn) {
      navigate("/login", { replace: true })
      return
    }
    handled.current = true

    const searchParams = new URLSearchParams(window.location.search)
    const rawNext = searchParams.get("next")

    const storedRole = (() => {
      try { return localStorage.getItem(ROLE_KEY) as "patient" | "doctor" | null } catch { return null }
    })()

    if (isDoctorHost() || storedRole) {
      // D2C path — provision app_users row on first sign-in
      const role = storedRole ?? "patient"
      const defaultDest = role === "doctor" ? "/app/cloud" : "/patient/dashboard"
      const dest = sanitizeNextPath(rawNext, defaultDest)

      runD2cSignup(role, api.authD2c).then((outcome) => {
        if (outcome.kind === "error") {
          console.error("[auth-callback] D2C provision failed:", outcome.message)
          setProvisionError(outcome.message)
          return
        }
        if (outcome.kind === "new") {
          track("login_completed", { method: "clerk_d2c", status: "new", role })
          try { localStorage.removeItem(ROLE_KEY) } catch {}
        } else {
          track("login_completed", { method: "clerk_d2c", status: "existing" })
        }
        navigate(dest, { replace: true })
      })
      return
    }

    // Clinic path — /auth/me auto-provisions
    const onboardingPath = "/app/onboarding"
    const home           = sanitizeNextPath(rawNext, "/app/dashboard")

    if (localStorage.getItem("snow_onboarded") === "true") {
      track("login_completed", { method: "clerk_clinic", status: "returning" })
      navigate(home, { replace: true })
      return
    }

    api.auth.me()
      .then((u) => {
        track("login_completed", { method: "clerk_clinic", status: "ok" })
        const configured = brandLooksConfigured((u as any).brand)
        if (configured) localStorage.setItem("snow_onboarded", "true")
        navigate(configured ? home : onboardingPath, { replace: true })
      })
      .catch(() => {
        navigate(onboardingPath, { replace: true })
      })
  }, [isLoaded, isSignedIn, navigate, provisionError])

  if (provisionError) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm rounded-xl border border-red-200 bg-white p-6 shadow-sm text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Setup Interrupted</h2>
          <p className="text-sm text-red-600 mb-4">{provisionError}</p>
          <div className="space-y-2">
            <button
              onClick={() => {
                setProvisionError(null)
                handled.current = false
              }}
              className="w-full py-2 px-4 rounded-lg bg-indigo-600 text-white font-medium text-sm hover:bg-indigo-700 transition"
            >
              Retry setup
            </button>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="w-full py-2 px-4 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 transition"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen items-center justify-center text-gray-400 text-sm">
      Signing you in…
    </div>
  )
}
